import React, { useState } from 'react';
import { Player } from '../../types';

interface TransferAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTransfer: (newOwnerId: string) => void;
  players: Player[];
  currentUserId: string;
  loading?: boolean;
}

export const TransferAdminModal: React.FC<TransferAdminModalProps> = ({
  isOpen,
  onClose,
  onTransfer,
  players,
  currentUserId,
  loading = false
}) => {
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // Filtrar jugadores que no sean el usuario actual
  const availablePlayers = players.filter(player => player.userId !== currentUserId);
  
  // Separar usuarios registrados de invitados
  const registeredUsers = availablePlayers.filter(player => player.userId);
  const guestUsers = availablePlayers.filter(player => !player.userId);

  const handleTransfer = () => {
    if (selectedPlayerId) {
      // Verificar que el jugador seleccionado sea un usuario registrado
      const selectedPlayer = registeredUsers.find(player => player.userId === selectedPlayerId);
      if (selectedPlayer) {
        onTransfer(selectedPlayerId);
      } else {
        // Esto no debería pasar, pero por seguridad
        // Handle error appropriately instead of console.error
        setError('Error: Usuario no registrado encontrado');
      }
    }
  };

  const handleClose = () => {
    setSelectedPlayerId('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#23272A',
        borderRadius: '16px',
        padding: '24px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
        border: '1px solid #33383B',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h2 style={{
            margin: 0,
            color: '#FFFFFF',
            fontSize: '20px',
            fontWeight: '600'
          }}>
            🏆 Transferir Administración
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#B0BEC5',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <p style={{
            color: '#B0BEC5',
            fontSize: '14px',
            lineHeight: '1.5',
            margin: '0 0 16px 0'
          }}>
            Selecciona el jugador al que quieres transferir la administración del partido. 
            Esta acción no se puede deshacer.
          </p>
        </div>

        {availablePlayers.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '20px',
            color: '#B0BEC5',
            fontSize: '14px'
          }}>
            No hay otros jugadores disponibles para transferir la administración.
          </div>
        ) : registeredUsers.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '20px',
            color: '#FF9800',
            fontSize: '14px'
          }}>
            ⚠️ Solo hay usuarios invitados en el partido. Para transferir la administración, necesitas usuarios registrados en la aplicación.
          </div>
        ) : (
          <div style={{ marginBottom: '24px' }} role="group" aria-labelledby="transfer-admin-label">
            <p id="transfer-admin-label" style={{
              display: 'block',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '12px',
              margin: '0 0 12px 0'
            }}>
              Seleccionar nuevo administrador:
            </p>
            
            {/* Usuarios registrados */}
            {registeredUsers.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  color: '#4CAF50',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  ✅ Usuarios Registrados (Pueden recibir administración)
                </div>
                <div style={{
                  maxHeight: '150px',
                  overflowY: 'auto',
                  border: '1px solid #4CAF50',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(76, 175, 80, 0.1)'
                }}>
                  {registeredUsers.map((player) => (
                    <button
                      key={player.id}
                      type="button"
                      onClick={() => setSelectedPlayerId(player.userId || '')}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: '1px solid rgba(76, 175, 80, 0.3)',
                        backgroundColor: selectedPlayerId === player.userId ? '#4CAF50' : 'transparent',
                        color: '#FFFFFF',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                        border: 'none',
                        textAlign: 'left'
                      }}
                    >
                      <div style={{
                        backgroundColor: '#4CAF50',
                        color: '#FFFFFF',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        flexShrink: 0
                      }}>
                        {player.name.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>
                          {player.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#B0BEC5' }}>
                          {player.position} • {'★'.repeat(player.rating)}
                        </div>
                      </div>
                      <div style={{
                        backgroundColor: '#4CAF50',
                        color: '#FFFFFF',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: '600'
                      }}>
                        REGISTRADO
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Usuarios invitados */}
            {guestUsers.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  color: '#FF9800',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  ⚠️ Usuarios Invitados (No pueden recibir administración)
                </div>
                <div style={{
                  maxHeight: '150px',
                  overflowY: 'auto',
                  border: '1px solid #FF9800',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  opacity: 0.6
                }}>
                  {guestUsers.map((player) => (
                    <div
                      key={player.id}
                      style={{
                        padding: '12px 16px',
                        cursor: 'not-allowed',
                        borderBottom: '1px solid rgba(255, 152, 0, 0.3)',
                        backgroundColor: 'transparent',
                        color: '#B0BEC5',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                    >
                      <div style={{
                        backgroundColor: '#FF9800',
                        color: '#FFFFFF',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        flexShrink: 0
                      }}>
                        {player.name.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>
                          {player.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#B0BEC5' }}>
                          {player.position} • {'★'.repeat(player.rating)}
                        </div>
                      </div>
                      <div style={{
                        backgroundColor: '#FF9800',
                        color: '#FFFFFF',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: '600'
                      }}>
                        INVITADO
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <div style={{
            color: '#FF5722',
            fontSize: '14px',
            marginTop: '16px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={handleClose}
            disabled={loading}
            style={{
              padding: '10px 20px',
              backgroundColor: '#33383B',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              opacity: loading ? 0.6 : 1
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleTransfer}
            disabled={!selectedPlayerId || loading || registeredUsers.length === 0}
            style={{
              padding: '10px 20px',
              backgroundColor: selectedPlayerId && !loading && registeredUsers.length > 0 ? '#FF5722' : '#666',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              cursor: selectedPlayerId && !loading && registeredUsers.length > 0 ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '600',
              opacity: selectedPlayerId && !loading && registeredUsers.length > 0 ? 1 : 0.6
            }}
          >
            {loading ? 'Transferiendo...' : registeredUsers.length === 0 ? 'No hay usuarios registrados' : 'Transferir Administración'}
          </button>
        </div>
      </div>
    </div>
  );
}; 